import React, { createContext, useContext, useState, useEffect } from 'react';
import gameRealtimeClient from '../services/gameRealtimeClient';
import logger from '../utils/logger';

const authLogger = logger.child({ context: 'AuthContext' });

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      return token && savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('token')));

  useEffect(() => {
    // Listen for global 401 unauthorized events to clean state immediately
    const handleUnauthorized = () => {
      setCurrentUser(null);
      gameRealtimeClient.disconnect();
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    // Check for stored token on mount, then re-validate with server
    const token = localStorage.getItem('token');

    if (token) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5025/api';
      fetch(`${baseUrl}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (data && data.success && data.data) {
            const freshUser = data.data;
            localStorage.setItem('user', JSON.stringify(freshUser));
            setCurrentUser(freshUser);
          } else {
            // Token is confirmed invalid / expired / revoked by backend — clear session
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setCurrentUser(null);
          }
        })
        .catch(() => {
          // Network error / offline
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  // Helper function for API calls
  const apiCall = async (endpoint, options = {}) => {
    // Use environment variable for base URL
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5025/api';
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      // Add default method if not specified
      if (!options.method) {
        options.method = 'GET';
      }
      
      const response = await fetch(`${baseUrl}${endpoint}`, {
        ...options,
        headers
      });

      // Always try to parse the response as JSON, regardless of status code
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        authLogger.error('Error parsing JSON response:', parseError);
        throw new Error('Invalid response from server: Could not parse JSON');
      }
      
      // If response is not ok, throw an error with the message from the server
      if (!response.ok) {
        throw new Error(data.message || `Server error: ${response.status}`);
      }
      
      // Return the parsed JSON data
      return data;
    } catch (error) {
      authLogger.debug('API call error:', { endpoint, error: error.message });
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error(`Network error: Could not connect to server at ${baseUrl}${endpoint}`);
      }
      throw error;
    }
  };

  // Function to handle user login
  const login = async (email, password) => {
    try {
      // Make sure we're sending the correct content type
      const response = await apiCall('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Check if the response has the expected structure
      if (response && response.success && response.data && response.data.token) {
        // Store the token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setCurrentUser(response.data.user);
        authLogger.info('User logged in successfully');
        return { success: true, user: response.data.user };
      } else {
        authLogger.warn('Invalid login response format');
        return { 
          success: false, 
          message: response?.message || 'Invalid response from server'
        };
      }
    } catch (error) {
      authLogger.error('Login error:', error);
      // Provide a more user-friendly error message
      let errorMessage = 'Failed to login';
      
      if (error.message === 'Invalid credentials') {
        errorMessage = 'Email or password is incorrect';
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  // Function to handle Google OAuth login
  const googleLogin = async (tokenOrCredential) => {
    try {
      const bodyPayload = typeof tokenOrCredential === 'object'
        ? tokenOrCredential
        : {
            credential: tokenOrCredential,
            access_token: tokenOrCredential,
            token: tokenOrCredential
          };

      const response = await apiCall('/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyPayload)
      });

      if (response && response.success && response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setCurrentUser(response.data.user);
        authLogger.info('Google user logged in successfully');
        return { success: true, user: response.data.user };
      } else {
        return {
          success: false,
          is_new_user: response?.is_new_user,
          oauth_profile: response?.oauth_profile,
          message: response?.message || 'Google authentication failed'
        };
      }
    } catch (error) {
      authLogger.error('Google login error:', error);
      return {
        success: false,
        message: error.message || 'Failed to authenticate with Google'
      };
    }
  };

  // Function to handle user registration
  const register = async (username, email, password, additionalData = {}) => {
    try {
      const registrationData = { 
        username, 
        email, 
        password, 
        ...additionalData
      };

      const data = await apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify(registrationData)
      });
      
      if (data.success) {
        const { user, token } = data.data;
        
        // Store token and user data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setCurrentUser(user);
        authLogger.info('User registered successfully');
        return { success: true, user };
      } else {
        return {
          success: false,
          message: data.message || 'Registration failed'
        };
      }
    } catch (error) {
      authLogger.error('Registration error:', error);
      return {
        success: false,
        message: error.message || 'Error registering user'
      };
    }
  };

  // Function to handle user logout with optional academy-custom redirection
  const logout = () => {
    let userObj = currentUser;
    if (!userObj) {
      try {
        const stored = localStorage.getItem('user');
        if (stored) {
          userObj = JSON.parse(stored);
        }
      } catch (e) {}
    }

    // Only apply academy custom logout redirect to academy students and academy sub-coaches (who belong to an academy with super_coach_id).
    // Individual coaches (who have no super_coach_id), platform admins, and general players are NEVER redirected.
    const isAcademyStudentOrSubCoach = Boolean(userObj?.super_coach_id) && !userObj?.is_admin;
    const customRedirect = isAcademyStudentOrSubCoach 
      ? (userObj?.academy_branding?.logout_redirect_url || userObj?.logout_redirect_url)
      : null;

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    gameRealtimeClient.disconnect();
    setCurrentUser(null);

    if (customRedirect && typeof customRedirect === 'string' && customRedirect.trim().length > 0) {
      let target = customRedirect.trim();
      if (!/^https?:\/\//i.test(target) && !target.startsWith('/')) {
        target = `https://${target}`;
      }
      window.location.href = target;
      return target;
    }
    return null;
  };

  // Update current user in state and localStorage (e.g. after profile picture change)
  const updateCurrentUser = (updatedFields) => {
    setCurrentUser(prev => {
      const updated = { ...prev, ...updatedFields };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

    // Function to handle admin login - uses real auth system
  const adminLogin = async (email, password) => {
    try {
      console.log('Admin login attempt with:', { email });
      
      // Instead of using fake tokens, actually authenticate with the backend
      const response = await apiCall('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Check if the response has the expected structure
      if (response && response.success && response.data && response.data.token) {
        const { user, token } = response.data;
        
        // Make sure the user is an admin
        if (!user.is_admin) {
          authLogger.warn('Login successful but user is not an admin');
          return {
            success: false,
            message: 'This account does not have admin privileges'
          };
        }
        
        // Store the token and user data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
        authLogger.info('Admin user authenticated successfully');
        
        return { success: true, user };
      } else {
        authLogger.warn('Invalid login response format for admin');
        return { 
          success: false, 
          message: response?.message || 'Invalid response from server'
        };
      }
    } catch (error) {
      authLogger.error('Admin login error:', error);
      // Provide a more user-friendly error message
      let errorMessage = 'Failed to login as admin';
      
      if (error.message === 'Invalid credentials') {
        errorMessage = 'Email or password is incorrect';
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  // Function to handle coach login - uses real auth system
  const coachLogin = async (email, password) => {
    try {
      // Authenticate with the backend
      const response = await apiCall('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Check if the response has the expected structure
      if (response && response.success && response.data && response.data.token) {
        const { user, token } = response.data;
        
        // Make sure the user is a coach
        if (!user.is_coach) {
          authLogger.warn('Login successful but user is not a coach');
          return {
            success: false,
            message: 'This account does not have coach privileges'
          };
        }
        
        // Store the token and user data
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
        authLogger.info('Coach user authenticated successfully');
        
        return { success: true, user };
      } else {
        authLogger.warn('Invalid login response format for coach');
        return { 
          success: false, 
          message: response?.message || 'Invalid response from server'
        };
      }
    } catch (error) {
      authLogger.error('Coach login error:', error);
      // Provide a more user-friendly error message
      let errorMessage = 'Failed to login as coach';
      
      if (error.message === 'Invalid credentials') {
        errorMessage = 'Email or password is incorrect';
      } else if (error.message.includes('Network error')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  // Helper function to get the current token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    currentUser,
    login,
    googleLogin,
    register,
    logout,
    updateCurrentUser,
    loading,
    adminLogin,
    coachLogin,
    getToken,
    apiCall
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};